export type ComponentTokenModule =
  | "alert"
  | "apple_map"
  | "badge"
  | "body"
  | "bold"
  | "brand_icon"
  | "brand_illustration"
  | "button"
  | "callout"
  | "checkbox_group"
  | "checkbox"
  | "collapse"
  | "drawer"
  | "dropdown_control"
  | "dropdown_group_heading"
  | "dropdown_group"
  | "dropdown_menu_list"
  | "dropdown_menu"
  | "dropdown_option"
  | "dropdown_placeholder"
  | "dropdown_single_value"
  | "dropdown_value_container"
  | "dropdown"
  | "file_upload"
  | "form_character_count"
  | "form_error"
  | "form_group_layout_wrapper"
  | "form_input_container"
  | "form_instructions"
  | "form_label"
  | "form"
  | "grid"
  | "heading"
  | "horizontal_line"
  | "icon"
  | "image"
  | "input_alignment_container"
  | "italic"
  | "link"
  | "logo"
  | "menu_button"
  | "menu_item"
  | "menu"
  | "message"
  | "modal"
  | "multi_select_dropdown"
  | "overlay"
  | "orderable_list"
  | "pager"
  | "panel"
  | "page_title"
  | "panel"
  | "paragraph"
  | "radio_group"
  | "radio"
  | "regular"
  | "search_textbox"
  | "semibold"
  | "shadow"
  | "shelf"
  | "sidebar_nav"
  | "spinner"
  | "stack"
  | "strikethrough"
  | "subheading"
  | "submit_button"
  | "subtitle"
  | "tabbed_container"
  | "table"
  | "tab_set"
  | "hotkey_badge"
  | "tag"
  | "telephone_textbox"
  | "text_area"
  | "textbox"
  | "title"
  | "tooltip"
  | "underlined";

const FOUNDATION_STYLE_ID = "capitalrx-token-theme-foundation";
const DATA_THEME_ATTR = "data-theme";
const DEFAULT_THEME = "caprx";

/* ---------------------------------------------------------------------------
   Module-Federation-safe global cache (I-07)

   In MF layouts each remote gets its own module instance with its own
   module-scope state.  We use a shared global (`window.__CRX_LOADED_TOKENS__`)
   as a secondary fast-path cache while the DOM marker (`<meta>` element)
   remains the authoritative source of truth for whether a component's CSS
   has already been injected.
   --------------------------------------------------------------------------- */

const GLOBAL_CACHE_KEY = "__CRX_LOADED_TOKENS__";

interface GlobalTokenCache {
  loaded: Set<string>;
  inFlight: Map<string, Promise<void>>;
}

declare global {
  interface Window {
    [GLOBAL_CACHE_KEY]?: GlobalTokenCache;
  }
}

function getGlobalCache(): GlobalTokenCache {
  if (typeof window !== "undefined") {
    if (!window[GLOBAL_CACHE_KEY]) {
      window[GLOBAL_CACHE_KEY] = { loaded: new Set(), inFlight: new Map() };
    }
    return window[GLOBAL_CACHE_KEY]!;
  }
  // SSR fallback — module-scope cache
  return _ssrFallbackCache;
}

const _ssrFallbackCache: GlobalTokenCache = { loaded: new Set(), inFlight: new Map() };

async function ensureThemeFoundationLoaded(): Promise<void> {
  if (typeof document === "undefined") return;

  const cache = getGlobalCache();
  if (cache.loaded.has(FOUNDATION_STYLE_ID) || document.getElementById(FOUNDATION_STYLE_ID)) {
    cache.loaded.add(FOUNDATION_STYLE_ID);
    return;
  }

  // Deduplicate concurrent calls for the foundation itself
  const existing = cache.inFlight.get(FOUNDATION_STYLE_ID);
  if (existing) {
    await existing;
  } else {
    const loadPromise = (async () => {
      try {
        await import(
          /* webpackChunkName: "token-theme-caprx" */
          // @ts-ignore TS2307: CSS is resolved by webpack at runtime in consuming shells.
          "../styles/global.css"
        );
        cache.loaded.add(FOUNDATION_STYLE_ID);

        if (!document.getElementById(FOUNDATION_STYLE_ID)) {
          const marker = document.createElement("meta");
          marker.id = FOUNDATION_STYLE_ID;
          marker.setAttribute("data-component", "theme-foundation");
          document.head.appendChild(marker);
        }
      } finally {
        cache.inFlight.delete(FOUNDATION_STYLE_ID);
      }
    })();
    cache.inFlight.set(FOUNDATION_STYLE_ID, loadPromise);
    await loadPromise;
  }

  if (!document.documentElement.getAttribute(DATA_THEME_ATTR)) {
    document.documentElement.setAttribute(DATA_THEME_ATTR, DEFAULT_THEME);
  }
}

// HMR support: reset state on hot reload
if (typeof module !== "undefined" && (module as unknown as { hot?: { dispose: (cb: () => void) => void } }).hot) {
  (module as unknown as { hot?: { dispose: (cb: () => void) => void } }).hot?.dispose(() => {
    const cache = getGlobalCache();
    cache.loaded.clear();
    cache.inFlight.clear();
  });
}

/**
 * Ensures the shared token theme foundation (global.css / semantic CSS vars)
 * is loaded for the given component's token CSS to resolve correctly.
 *
 * Per-component token CSS is delivered directly via each component's own
 * `styles.module.css`; this function only guarantees the theme foundation is
 * present. The `component` parameter is retained for API compatibility.
 */
export async function loadComponentTokens(_component: ComponentTokenModule): Promise<void> {
  await ensureThemeFoundationLoaded();
}
export async function loadComponentTokensBatch(components: ComponentTokenModule[]): Promise<void> {
  await Promise.all(components.map((component) => loadComponentTokens(component)));
}
/**
 * @deprecated Component tokens are now delivered via each component's own
 * `styles.module.css` and are not tracked here. Always returns `false`.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function isComponentTokensLoaded(_component: ComponentTokenModule): boolean {
  return false;
}
/**
 * Clears the in-memory loaded-tokens cache **and** removes the injected
 * theme-foundation DOM marker (`<meta id="capitalrx-token-theme-foundation">`).
 *
 * After calling this, subsequent `loadComponentTokens()` calls will re-import
 * the theme foundation CSS and re-create the DOM marker.
 */
export function clearLoadedComponentsCache(): void {
  const cache = getGlobalCache();
  cache.loaded.clear();

  if (typeof document !== "undefined") {
    document.getElementById(FOUNDATION_STYLE_ID)?.remove();
  }
}
